// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import logo from '../assets/al-konda-logo--647x500.png';

// const LoadingIndicator = ({ size = 'large', showText = true, className = '' }) => {
//   // Sizes optimized for pixel perfection
//   const sizeConfig = {
//     small:  { box: 60,  logo: 24, text: 'text-[10px]' },
//     medium: { box: 90,  logo: 36, text: 'text-xs' },
//     large:  { box: 120, logo: 48, text: 'text-sm' }
//   };

//   const config = sizeConfig[size];
//   const center = config.box / 2;
//   const radius = (config.box / 2) - 4; // Slight padding
//   const circumference = 2 * Math.PI * radius;

//   // Simple text cycler - removed the heavy blur transition
//   const [textIndex, setTextIndex] = useState(0);
//   const messages = ['SYSTEM SYNC', 'VERIFYING', 'LOADING ASSETS', 'AL-KONDA'];

//   useEffect(() => {
//     if (!showText) return;
//     const timer = setInterval(() => {
//       setTextIndex((prev) => (prev + 1) % messages.length);
//     }, 2000);
//     return () => clearInterval(timer);
//   }, [showText]);

//   return (
//     <div className={`flex flex-col items-center justify-center ${className}`}>

//       {/* Main Composition */}
//       <div 
//         className="relative flex items-center justify-center"
//         style={{ width: config.box, height: config.box }}
//       >

//         {/* 1. Static Atmosphere (No animation cost) */}
//         <div className="absolute inset-0 bg-blue-500/5 rounded-full blur-2xl" />

//         {/* 2. The Rotating Arc (GPU Accelerated) */}
//         {/* We use a simple linear rotation which is cheap for the browser */}
//         <motion.div
//           className="absolute inset-0 w-full h-full will-change-transform"
//           animate={{ rotate: 360 }}
//           transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
//         >
//           <svg className="w-full h-full transform -rotate-90">
//             <defs>
//               <linearGradient id="gradientArc" x1="0%" y1="0%" x2="100%" y2="0%">
//                 <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
//                 <stop offset="100%" stopColor="rgba(255, 255, 255, 0.9)" />
//               </linearGradient>
//             </defs>
//             {/* Background Track */}
//             <circle
//               cx={center} cy={center} r={radius}
//               fill="transparent"
//               stroke="rgba(255, 255, 255, 0.05)"
//               strokeWidth="1"
//             />
//             {/* Active Rotating Gradient Arc */}
//             <circle
//               cx={center} cy={center} r={radius}
//               fill="transparent"
//               stroke="url(#gradientArc)"
//               strokeWidth="1.5"
//               strokeDasharray={circumference}
//               strokeDashoffset={circumference * 0.25} // 75% circle
//               strokeLinecap="round"
//             />
//           </svg>
//         </motion.div>

//         {/* 3. Counter-Rotating Inner Ring (Adds complexity without cost) */}
//         <motion.div
//           className="absolute inset-0 w-full h-full flex items-center justify-center will-change-transform"
//           animate={{ rotate: -360 }}
//           transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
//         >
//           <div 
//             className="rounded-full border border-dashed border-white/20"
//             style={{ width: config.box * 0.7, height: config.box * 0.7 }}
//           />
//         </motion.div>

//         {/* 4. Center Logo Container (Glass Effect) */}
//         <div 
//           className="relative z-10 flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-full border border-white/10 shadow-2xl"
//           style={{ width: config.box * 0.5, height: config.box * 0.5 }}
//         >
//           <motion.img
//             src={logo}
//             alt="Loading"
//             className="object-contain opacity-90 will-change-transform"
//             style={{ width: config.logo, height: config.logo }}
//             animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
//             transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
//           />
//         </div>
//       </div>

//       {/* 5. Optimized Text Animation */}
//       {showText && (
//         <div className="h-8 mt-6 flex items-center justify-center overflow-hidden">
//           <AnimatePresence mode="wait">
//             <motion.div
//               key={textIndex}
//               // Only animating opacity and y-position (very cheap)
//               initial={{ opacity: 0, y: 5 }}
//               animate={{ opacity: 1, y: 0 }}
//               exit={{ opacity: 0, y: -5 }}
//               transition={{ duration: 0.3 }}
//               className={`font-mono ${config.text} tracking-[0.25em] text-white/80 whitespace-nowrap`}
//             >
//               {messages[textIndex]}
//             </motion.div>
//           </AnimatePresence>
//         </div>
//       )}

//       {/* 6. Static Progress Line (Optional decoration) */}
//       <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent mt-2" />
//     </div>
//   );
// };

// export default LoadingIndicator;

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/al-konda-logo-optimized.webp';

const LoadingIndicator = ({ size = 'large', showText = true, className = '' }) => {

  // Sizes optimized for pixel perfection
  const sizeConfig = {
    small: { box: 60, logo: 24, text: 'text-[10px]' },
    medium: { box: 90, logo: 36, text: 'text-xs' },
    large: { box: 120, logo: 48, text: 'text-sm' }
  };

  const config = sizeConfig[size];
  const center = config.box / 2;
  const radius = (config.box / 2) - 3; // Reduced padding for a larger circle
  const circumference = 2 * Math.PI * radius;

  // Define a short arc length (e.g., 20% of the circle)
  const arcLength = circumference * 0.20;

  const [textIndex, setTextIndex] = useState(0);
  const messages = ['LOADING', 'LOADING ASSETS', 'AL-KONDA'];

  useEffect(() => {
    if (!showText) return;
    const timer = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % messages.length);
    }, 2000); // Faster text cycle
    return () => clearInterval(timer);
  }, [showText]);

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>

      {/* Main Composition */}
      <div
        className="relative flex items-center justify-center"
        style={{ width: config.box, height: config.box }}
      >

        {/* 1. Static Atmosphere (Removed background) */}

        {/* 2. The Fluid Arc Spinner (Two concurrent animations) */}
        <motion.div
          className="absolute inset-0 w-full h-full will-change-transform"
          // Primary animation: Constant rotation (smooth and fast)
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        >
          <svg className="w-full h-full transform -rotate-90">
            <defs>
              {/* Premium Gradient: Blue/White for a clean energy look */}
              <linearGradient id="fluidArc" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(0, 191, 255, 0.0)" />
                <stop offset="50%" stopColor="rgba(255, 255, 255, 0.8)" />
                <stop offset="100%" stopColor="rgba(0, 191, 255, 1.0)" />
              </linearGradient>
            </defs>

            {/* Background Track (Almost invisible) */}
            <circle
              cx={center} cy={center} r={radius}
              fill="transparent"
              stroke="rgba(255, 255, 255, 0.05)"
              strokeWidth="1"
            />

            {/* Active Fluid Arc - Uses Framer Motion for DASH OFFSET animation */}
            <motion.circle
              cx={center} cy={center} r={radius}
              fill="transparent"
              stroke="url(#fluidArc)"
              strokeWidth="2.5"
              strokeLinecap="round"
              // Set dash array to arc length + a small gap
              strokeDasharray={`${arcLength} ${circumference - arcLength}`}

              // Secondary animation: Draws/Erases the arc fluidly
              animate={{
                // Cycles between nearly full circumference (hidden) and back
                strokeDashoffset: [0, circumference * 0.99, 0]
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </svg>
        </motion.div>

        {/* 3. Center Logo Container (Glassmorphism) */}
        <motion.div
          className="relative z-10 flex items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-md"
          style={{
            width: config.box * 0.5,
            height: config.box * 0.5,
            boxShadow: '0 0 10px rgba(0, 191, 255, 0.5)'
          }}
          animate={{
            borderColor: ['rgba(255,255,255, 0.2)', 'rgba(0, 191, 255, 0.5)', 'rgba(255,255,255, 0.2)'],
            boxShadow: [
              '0 0 5px rgba(0, 191, 255, 0.0)',
              '0 0 15px rgba(0, 191, 255, 0.5)',
              '0 0 5px rgba(0, 191, 255, 0.0)'
            ]
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.img
            src={logo}
            alt="Loading"
            className="object-contain opacity-95 will-change-transform"
            style={{ width: config.logo, height: config.logo }}
            animate={{ scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </div>

      {/* 4. Optimized Text Animation */}
      {showText && (
        <div className="h-8 mt-6 flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={textIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: "easeOut" }} // Slightly faster transition
              className={`font-mono ${config.text} tracking-[0.25em] text-white/90 whitespace-nowrap`}
            >
              {messages[textIndex]}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* 5. Static Progress Line */}
      <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-blue-300/60 to-transparent mt-2" />
    </div>
  );
};

export default LoadingIndicator;